import { useEffect, useState } from "react";
import { getData, insertData, updateData, deleteData } from "../api";
import {
  Box,
  Button,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Chip,
  Divider,
} from "@mui/material";
import { Edit, Delete, Add } from "@mui/icons-material";
import theme from "../styles/theme";

interface Exercise {
  id: number;
  title: string;
  category_id: number;
}

interface Session {
  id: number;
  name: string;
  start_date: string;
  team: number;
  description?: string;
}

export default function SessionsPage() {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentSession, setCurrentSession] = useState<any>(null);
  
  const [formData, setFormData] = useState({
    name: "",
    start_date: "",
    team: "",
    description: "",
  });

  const [selectedExercises, setSelectedExercises] = useState<Map<number, number>>(new Map());
  const [exerciseDialogOpen, setExerciseDialogOpen] = useState(false);

  useEffect(() => {
    fetchSessions();
    fetchExercises();
    fetchTeams();
    fetchCategories();
  }, []);

  const fetchSessions = async () => {
    const res = await getData("sessions", { order_by: "start_date DESC" });
    setSessions(res.data || []);
  };

  const fetchExercises = async () => {
    const res = await getData("exercises");
    setExercises(res.data || []);
  };

  const fetchTeams = async () => {
    const res = await getData("teams");
    setTeams(res.data || []);
  };

  const fetchCategories = async () => {
    const res = await getData("exercise_categories");
    setCategories(res.data || []);
  };

  const handleOpen = () => {
    setEditMode(false);
    setCurrentSession(null);
    setFormData({ name: "", start_date: "", team: "", description: "" });
    setSelectedExercises(new Map());
    setOpen(true);
  };

  const handleEdit = async (session: Session) => {
    setEditMode(true);
    setCurrentSession(session);
    setFormData({
      name: session.name,
      start_date: session.start_date,
      team: session.team.toString(),
      description: session.description || "",
    });

    // Charger les exercices de la session
    const res = await getData("session_exercises", { where: `session_id = ${session.id}` });
    const sessionExercises = res.data || [];
    const exerciseMap = new Map<number, number>();
    sessionExercises.forEach((se: any) => {
      exerciseMap.set(se.exercise_id, se.coefficient);
    });
    setSelectedExercises(exerciseMap);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setExerciseDialogOpen(false);
  };

  const handleSave = async () => {
    if (!formData.name || !formData.start_date || !formData.team) {
      alert("Veuillez remplir tous les champs obligatoires");
      return;
    }

    try {
      if (editMode && currentSession) {
        await updateData("sessions", formData, `id = ${currentSession.id}`);
        
        // Supprimer les anciens exercices de la session
        await deleteData("session_exercises", `session_id = ${currentSession.id}`);
        
        // Ajouter les nouveaux exercices avec leurs coefficients
        for (const [exerciseId, coefficient] of Array.from(selectedExercises.entries())) {
          await insertData("session_exercises", {
            session_id: currentSession.id,
            exercise_id: exerciseId,
            coefficient,
          });
        }
      } else {
        const res = await insertData("sessions", formData);
        console.log("Réponse insertData:", res);
        
        // L'API renvoie {success: true, inserted: 1} mais pas l'ID
        // On doit récupérer la session créée
        const sessionsRes = await getData("sessions", { 
          where: `name = '${formData.name}' AND start_date = '${formData.start_date}' AND team = ${formData.team}`,
          order_by: "id DESC",
          limit: 1
        });
        
        if (!sessionsRes || !sessionsRes.data || sessionsRes.data.length === 0) {
          throw new Error("Impossible de récupérer la session créée");
        }
        
        const newSessionId = sessionsRes.data[0].id;
        console.log("ID de la nouvelle session:", newSessionId);
        
        // Ajouter les exercices sélectionnés
        for (const [exerciseId, coefficient] of Array.from(selectedExercises.entries())) {
          await insertData("session_exercises", {
            session_id: newSessionId,
            exercise_id: exerciseId,
            coefficient,
          });
        }
      }
      
      fetchSessions();
      handleClose();
    } catch (error) {
      console.error("Erreur lors de l'enregistrement:", error);
      alert(`Erreur lors de l'enregistrement de la session: ${error instanceof Error ? error.message : 'Erreur inconnue'}`);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Supprimer cette session ? Toutes les évaluations associées seront également supprimées.")) {
      try {
        await deleteData("sessions", `id = ${id}`);
        fetchSessions();
      } catch (error) {
        console.error("Erreur lors de la suppression:", error);
        alert("Erreur lors de la suppression de la session");
      }
    }
  };

  const toggleExercise = (exerciseId: number) => {
    const newMap = new Map(selectedExercises);
    if (newMap.has(exerciseId)) {
      newMap.delete(exerciseId);
    } else {
      newMap.set(exerciseId, 1.0);
    }
    setSelectedExercises(newMap);
  };

  const updateCoefficient = (exerciseId: number, coefficient: number) => {
    const newMap = new Map(selectedExercises);
    newMap.set(exerciseId, coefficient);
    setSelectedExercises(newMap);
  };

  const getCategoryName = (categoryId: number) => {
    return categories.find(c => c.id === categoryId)?.name || "";
  };

  const groupedExercises = exercises.reduce((acc, exercise) => {
    const categoryId = exercise.category_id;
    if (!acc[categoryId]) {
      acc[categoryId] = [];
    }
    acc[categoryId].push(exercise);
    return acc;
  }, {} as Record<number, Exercise[]>);

  return (
    <Box sx={{ p: 4, maxWidth: 1200, margin: "0 auto" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h1" sx={{ fontSize: "2rem", fontWeight: "bold", color: theme.palette.primary.main }}>
          Gestion des Sessions
        </Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleOpen}>
          Nouvelle Session
        </Button>
      </Box>

      <List>
        {sessions.map((session) => {
          const team = teams.find(t => t.id === session.team);
          return (
            <ListItem
              key={session.id}
              sx={{
                mb: 2,
                borderRadius: theme.shape.borderRadius,
                background: theme.palette.background.paper,
                boxShadow: "0 1px 6px rgba(0,0,0,0.04)",
              }}
              secondaryAction={
                <Box>
                  <IconButton edge="end" onClick={() => handleEdit(session)} sx={{ mr: 1 }}>
                    <Edit />
                  </IconButton>
                  <IconButton edge="end" onClick={() => handleDelete(session.id)}>
                    <Delete />
                  </IconButton>
                </Box>
              }
            >
              <ListItemText
                primary={
                  <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                    {session.name}
                  </Typography>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Date: {session.start_date} | Équipe: {team?.name || "N/A"}
                    </Typography>
                    {session.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {session.description}
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          );
        })}
      </List>

      {/* Dialog pour créer/éditer une session */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {editMode ? "Modifier la Session" : "Nouvelle Session"}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Nom de la session"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
            required
          />
          <TextField
            fullWidth
            type="date"
            label="Date de début"
            value={formData.start_date}
            onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
            required
          />
          <FormControl fullWidth sx={{ mb: 2 }} required>
            <InputLabel>Équipe</InputLabel>
            <Select
              value={formData.team}
              label="Équipe"
              onChange={(e) => setFormData({ ...formData, team: e.target.value })}
            >
              {teams.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  {team.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            fullWidth
            label="Description (optionnelle)"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            multiline
            rows={2}
            sx={{ mb: 3 }}
          />

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">
              Exercices sélectionnés ({selectedExercises.size})
            </Typography>
            <Button variant="outlined" onClick={() => setExerciseDialogOpen(true)}>
              Gérer les exercices
            </Button>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {Array.from(selectedExercises.entries()).map(([exerciseId, coefficient]) => {
              const exercise = exercises.find(e => e.id === exerciseId);
              return (
                <Chip
                  key={exerciseId}
                  label={`${exercise?.title} (coef: ${coefficient})`}
                  onDelete={() => toggleExercise(exerciseId)}
                  color="primary"
                  variant="outlined"
                />
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Annuler</Button>
          <Button onClick={handleSave} variant="contained">
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog pour gérer les exercices */}
      <Dialog open={exerciseDialogOpen} onClose={() => setExerciseDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Sélectionner et configurer les exercices</DialogTitle>
        <DialogContent>
          {Object.entries(groupedExercises).map(([categoryId, categoryExercises]) => (
            <Box key={categoryId} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1, color: theme.palette.primary.main }}>
                {getCategoryName(parseInt(categoryId))}
              </Typography>
              <List>
                {categoryExercises.map((exercise) => {
                  const isSelected = selectedExercises.has(exercise.id);
                  const coefficient = selectedExercises.get(exercise.id) || 1.0;
                  
                  return (
                    <ListItem key={exercise.id} sx={{ py: 1 }}>
                      <Checkbox
                        checked={isSelected}
                        onChange={() => toggleExercise(exercise.id)}
                      />
                      <ListItemText primary={exercise.title} sx={{ flex: 1 }} />
                      {isSelected && (
                        <TextField
                          type="number"
                          label="Coefficient"
                          value={coefficient}
                          onChange={(e) => updateCoefficient(exercise.id, parseFloat(e.target.value) || 1.0)}
                          inputProps={{ min: 0.5, max: 5, step: 0.5 }}
                          sx={{ width: 120 }}
                          size="small"
                        />
                      )}
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExerciseDialogOpen(false)} variant="contained">
            Terminé
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
